import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Empty, Row, Select, Spin, Statistic, Tag } from "antd";
import { useLocation } from "react-router-dom";
import { ComponentTitle } from "../../custom-components";
import MonthGrid from "./MonthGrid";
import DayDetailPanel from "./DayDetailPanel";
import { aggregate } from "./aggregations";
import { useBatchJobCalendarData } from "./hooks/useBatchJobCalendarData";

const BatchJobsCalendarComponent: React.FC = () => {
    const location = useLocation();
    const [batchJobId, setBatchJobId] = useState<string | null>(null);
    const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setBatchJobId(params.get("batchJobId"));
    }, [location.search]);

    const { loading, err, allRows, scheduleData, strategies } = useBatchJobCalendarData(batchJobId);

    useEffect(() => {
        if (!selectedStrategy && strategies.length > 0) {
            setSelectedStrategy(strategies[0]);
        }
    }, [strategies, selectedStrategy]);

    const filteredRows = useMemo(() => {
        if (!selectedStrategy) return allRows;
        return allRows.filter((r) => r.STRATEGY_NAME === selectedStrategy);
    }, [allRows, selectedStrategy]);

    const summary = useMemo(
        () => aggregate(filteredRows, scheduleData),
        [filteredRows, scheduleData],
    );

    // Default the day-detail panel to the first day of the data set, but only
    // if no day is currently selected or the selection is no longer present.
    useEffect(() => {
        if (summary.days.length === 0) {
            setSelectedKey(null);
            return;
        }
        if (!selectedKey || !summary.byKey[selectedKey]) {
            setSelectedKey(summary.days[0].isoKey);
        }
    }, [summary, selectedKey]);

    const selectedDay = selectedKey ? summary.byKey[selectedKey] ?? null : null;
    const program = scheduleData[Object.keys(scheduleData)[0]]?.PROGRAM;
    const dateRangeLabel = summary.days.length > 0
        ? `${summary.days[0].dateStr} → ${summary.days[summary.days.length - 1].dateStr}`
        : "—";

    return (
        <>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <ComponentTitle>Batch Jobs | Calendar View</ComponentTitle>
                </Col>
                <Col>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {strategies.length > 0 && (
                            <Select
                                value={selectedStrategy ?? undefined}
                                onChange={setSelectedStrategy}
                                style={{ minWidth: 220 }}
                                options={strategies.map((s) => ({ label: s, value: s }))}
                                placeholder="Strategy"
                            />
                        )}
                        {program && <Tag>{program}</Tag>}
                        {batchJobId && (
                            <span style={{ color: "#8c8c8c", fontSize: 12, fontFamily: "monospace" }}>
                                {batchJobId}
                            </span>
                        )}
                    </div>
                </Col>
            </Row>

            {!batchJobId && (
                <Alert
                    type="info"
                    showIcon
                    message="No batch job selected"
                    description="Open this page from a batch job's actions, or append ?batchJobId=… to the URL."
                />
            )}

            {batchJobId && err && (
                <Alert type="error" showIcon message="Failed to load batch job" description={err} style={{ marginBottom: 16 }} />
            )}

            {batchJobId && (
                <Spin spinning={loading}>
                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={4}>
                                <Statistic
                                    title="Date range"
                                    value={dateRangeLabel}
                                    valueStyle={{ fontSize: 14 }}
                                />
                            </Col>
                            <Col span={4}>
                                <Statistic title="Breaks" value={summary.totalSlots} />
                            </Col>
                            <Col span={4}>
                                <Statistic
                                    title="Overall fill"
                                    value={summary.fillPct}
                                    suffix="%"
                                    precision={1}
                                    valueStyle={{
                                        color:
                                            summary.fillPct >= 95 ? "#389e0d" :
                                                summary.fillPct >= 70 ? "#d48806" : "#cf1322",
                                    }}
                                />
                            </Col>
                            <Col span={4}>
                                <Statistic
                                    title="Total unfilled"
                                    value={summary.totalUnfilled}
                                    suffix="s"
                                    valueStyle={{ color: summary.totalUnfilled === 0 ? "#389e0d" : "#cf1322" }}
                                />
                            </Col>
                            <Col span={4}>
                                <Statistic
                                    title="Total $"
                                    value={summary.totalDollars}
                                    prefix="$"
                                    valueStyle={{ color: "#389e0d" }}
                                />
                            </Col>
                            <Col span={4}>
                                <Statistic
                                    title="Days with max_per_day violations"
                                    value={summary.daysWithViolations}
                                    valueStyle={{ color: summary.daysWithViolations > 0 ? "#cf1322" : "#389e0d" }}
                                />
                            </Col>
                        </Row>
                    </Card>

                    {summary.days.length === 0 && !loading ? (
                        <Card>
                            <Empty description={selectedStrategy
                                ? `No rows for strategy "${selectedStrategy}"`
                                : "No autopodder results for this batch job"} />
                        </Card>
                    ) : (
                        <Row gutter={16}>
                            <Col xs={24} xl={15}>
                                <Card bodyStyle={{ background: "#f0f0f0", borderRadius: 8 }}>
                                    <MonthGrid
                                        summary={summary}
                                        selectedKey={selectedKey}
                                        onSelectDay={setSelectedKey}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} xl={9}>
                                <DayDetailPanel day={selectedDay} scheduleData={scheduleData} />
                            </Col>
                        </Row>
                    )}
                </Spin>
            )}
        </>
    );
};

export default BatchJobsCalendarComponent;
